import type { LinksFunction, MetaFunction } from "@remix-run/node"

import * as UI from "@iyk/ui"
import * as React from "react"

import AppleTouchIconPngUrl from "@iyk/ui/assets/apple-touch-icon.png?url"
import FaviconSvgUrl from "@iyk/ui/assets/favicon.svg?url"
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react"
import * as SentryRemix from "@sentry/remix"
import { Effect, Option } from "effect"
import { LoaderResponse } from "../../../packages/remix/src/index.ts"
import stylesheet from "./app.css?url"
import { Remix } from "./server/main.ts"
import { Assets } from "./shared/assets.ts"
import { OpenGraph } from "./shared/open-graph.ts"
import { SentryClientScript } from "./shared/sentry-monitoring.tsx"

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "apple-touch-icon", sizes: "180x180", href: AppleTouchIconPngUrl },
  { rel: "icon", type: "image/svg+xml", href: FaviconSvgUrl },
]

export const meta: MetaFunction<typeof loader> = ({ location }) => {
  return OpenGraph.generateProperties({
    pathname: location.pathname,
    type: "website",
  })
}

export const loader = Remix.loader.handler(({ capabilities: { config, toast } }) =>
  Effect.gen(function* () {
    const toastCookie = yield* toast.consume
    return LoaderResponse.Ok({
      toast: Option.getOrNull(toastCookie),
      sentryConfig: Option.getOrNull(config.sentry),
      appUrl: config.appUrl,
    })
  }),
)

function App() {
  const { toast, sentryConfig } = useLoaderData<typeof loader>()

  React.useEffect(() => {
    if (toast && toast.state === "error") {
      UI.toast({
        title: toast.title,
        description: toast.description,
        variant: "destructive",
      })
    }
  }, [toast])

  return (
    <html lang="en" className="light text-gray-12">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <Outlet />
        <ScrollRestoration />
        <Scripts />
        {sentryConfig && <SentryClientScript {...sentryConfig} />}
        <UI.Toaster />
      </body>
    </html>
  )
}

export default SentryRemix.withSentry(App)

export function ErrorBoundary() {
  const error = useRouteError()

  SentryRemix.captureRemixErrorBoundaryError(error)

  let message = "Oops!"
  let details = "An unexpected error occurred."

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "Not Found" : "Error"
    details =
      error.status === 404 ? "The requested page could not be found." : error.statusText || details
  } else if (error && error instanceof Error) {
    if (import.meta.env.DEV) {
      message = error.name
      details = error.message
    }
  }

  return (
    <html lang="en" className="light text-gray-12">
      <head>
        <title>Error</title>
        <Meta />
        <Links />
      </head>
      <body>
        <main className="min-h-dvh container p-4 mx-auto flex flex-col items-center justify-center text-center text-sm">
          <img
            src={Assets.images.logo}
            alt="Krost logo"
            className="w-20 object-contain fixed top-4 left-1/2 -translate-x-1/2"
          />
          <h1 className="mb-2">{message}</h1>
          <p className="text-gray-11">{details}</p>
        </main>
        <Scripts />
      </body>
    </html>
  )
}
